import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  GenerateGrowthPositioningResponse,
  GrowthBrandVoiceProfileListItem,
  GrowthJourneyService,
  GrowthSwotLinkedArtifacts
} from './growth-journey.service';
import {
  brandVoiceTonePresetI18nKey,
  isStrategyArtifactApproved,
  swotProviderI18nKey
} from './growth-status.util';

@Component({
  standalone: true,
  selector: 'ain-growth-discovery-positioning-panel',
  providers: [MessageService],
  imports: [
    FormsModule,
    RouterLink,
    AinFormFieldComponent,
    InfoHintComponent,
    AinStateComponent,
    ButtonModule,
    SelectModule,
    TagModule
  ],
  template: `
    <section id="positioning" class="positioning-panel">
      <div class="section-title">
        <h3>{{ t('growth.journey.positioning.panel.title') }}</h3>
      </div>

      <ain-info-hint
        [titleKey]="'growth.journey.positioning.help.title'"
        [descriptionKey]="'growth.journey.positioning.help.description'"
        [bulletKeys]="[
          'growth.journey.positioning.help.bullets.draft',
          'growth.journey.positioning.help.bullets.approval'
        ]" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (autoGenerateWhenEmpty && generating() && !positioningArtifactId()) {
        <ain-state type="loading" [title]="t('growth.workflow.surfaces.journey.positioning.autoGenerating')" />
      } @else {
        @if (lastGenerateResult(); as result) {
          <div class="meta-row">
            <p-tag [value]="providerLabel(result.provider)" severity="info" />
            @if (result.usedFallback) {
              <p-tag [value]="t('growth.journey.positioning.fallbackUsed')" severity="warn" />
            }
            <p-tag
              [value]="t('growth.journey.positioning.fields.recommendedTone') + ': ' + t(tonePresetKey(result.recommendedTonePreset))"
              severity="secondary" />
          </div>
        }

        @if (positioningArtifactId(); as artifactId) {
          <div class="artifact-links">
            <a
              pButton
              severity="secondary"
              [routerLink]="['/growth/agents/strategy-artifacts', artifactId]"
              [label]="t('growth.journey.positioning.artifacts.review')"></a>
          </div>
        }

        @if (canGenerate()) {
          <ain-form-field [label]="t('growth.journey.positioning.fields.brandVoiceProfile')">
            <p-select
              [options]="brandVoiceOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="selectedBrandVoiceProfileId"
              [disabled]="readOnly"
              [showClear]="true"
              appendTo="body"
              [placeholder]="t('growth.journey.positioning.fields.brandVoiceProfilePlaceholder')" />
          </ain-form-field>

          <div class="actions">
            <button
              pButton
              type="button"
              [label]="t('growth.journey.positioning.generate')"
              [loading]="generating()"
              (click)="generate()"></button>
          </div>
        } @else if (!swotApproved()) {
          <p class="hint-text">{{ t('growth.journey.positioning.errors.swotNotApproved') }}</p>
          @if (artifacts()?.swot?.id; as swotArtifactId) {
            <div class="artifact-links">
              <a
                pButton
                severity="secondary"
                [routerLink]="['/growth/agents/strategy-artifacts', swotArtifactId]"
                [label]="t('growth.journey.positioning.artifacts.reviewSwot')"></a>
            </div>
          }
        }
      }

      @if (actionError()) {
        <p class="action-error">{{ actionError() }}</p>
      }
    </section>
  `,
  styles: [`
    .positioning-panel { border-top: 1px solid #e5e7eb; padding-top: 12px; display: grid; gap: 12px; }
    .section-title h3 { margin: 0; }
    .meta-row { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .artifact-links, .actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .action-error { margin: 0; color: #b91c1c; }
    .hint-text { margin: 0; color: #6b7280; }
  `]
})
export class GrowthDiscoveryPositioningPanelComponent implements OnInit, OnChanges {
  @Input({ required: true }) discoveryId!: string;
  @Input() latestPositioningArtifactId: string | null | undefined = null;
  @Input() autoGenerateWhenEmpty = false;
  @Input() readOnly = false;
  @Output() discoveryChanged = new EventEmitter<void>();

  private autoGenerateAttempted = false;

  private readonly journey = inject(GrowthJourneyService);
  private readonly i18n = inject(LocalizationService);
  private readonly messages = inject(MessageService);

  readonly loading = signal(false);
  readonly generating = signal(false);
  readonly actionError = signal<string | null>(null);
  readonly artifacts = signal<GrowthSwotLinkedArtifacts | null>(null);
  readonly lastGenerateResult = signal<GenerateGrowthPositioningResponse | null>(null);
  readonly positioningArtifactId = signal<string | null>(null);
  readonly brandVoiceProfiles = signal<GrowthBrandVoiceProfileListItem[]>([]);

  selectedBrandVoiceProfileId: string | null = null;

  ngOnInit(): void {
    this.positioningArtifactId.set(this.latestPositioningArtifactId ?? null);
    this.loadContext();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['latestPositioningArtifactId']) {
      this.positioningArtifactId.set(this.latestPositioningArtifactId ?? null);
    }

    if (changes['discoveryId'] && !changes['discoveryId'].firstChange) {
      this.loadContext();
    }
  }

  swotApproved(): boolean {
    const swot = this.artifacts()?.swot;
    return !!swot && isStrategyArtifactApproved(swot.status);
  }

  canGenerate(): boolean {
    return !this.readOnly && this.swotApproved() && !this.generating();
  }

  brandVoiceOptions(): Array<{ label: string; value: string }> {
    return this.brandVoiceProfiles()
      .filter(item => item.status !== 'Archived')
      .map(item => ({
        label: `${item.name} (${this.t(brandVoiceTonePresetI18nKey(item.tonePreset))})`,
        value: item.id
      }));
  }

  tonePresetKey(preset: string): string {
    return brandVoiceTonePresetI18nKey(preset);
  }

  providerLabel(provider: string): string {
    return this.t(swotProviderI18nKey(provider));
  }

  generate(): void {
    this.generateInternal(false);
  }

  t(key: string): string {
    return this.i18n.translate(key);
  }

  private loadContext(): void {
    this.loading.set(true);
    this.actionError.set(null);

    this.journey.getSwotArtifacts(this.discoveryId).subscribe({
      next: linked => {
        this.artifacts.set(linked);
        this.tryAutoGenerate();
      },
      error: err => {
        this.loading.set(false);
        this.actionError.set(this.resolveActionError(err));
      }
    });

    this.journey.listBrandVoiceProfiles().subscribe({
      next: profiles => this.brandVoiceProfiles.set(profiles),
      error: () => this.brandVoiceProfiles.set([])
    });
  }

  private tryAutoGenerate(): void {
    if (!this.autoGenerateWhenEmpty || this.autoGenerateAttempted || this.readOnly || this.positioningArtifactId()) {
      this.loading.set(false);
      return;
    }

    this.autoGenerateAttempted = true;
    if (!this.canGenerate()) {
      this.loading.set(false);
      return;
    }

    this.loading.set(false);
    this.generateInternal(true);
  }

  private generateInternal(isAuto: boolean): void {
    if (!this.canGenerate()) {
      return;
    }

    this.generating.set(true);
    this.actionError.set(null);

    this.journey.generatePositioning(this.discoveryId, {
      idempotencyKey: isAuto ? `positioning-auto-${this.discoveryId}` : `positioning-${this.discoveryId}-${Date.now()}`,
      brandVoiceProfileId: this.selectedBrandVoiceProfileId
    }).subscribe({
      next: result => {
        this.lastGenerateResult.set(result);
        this.positioningArtifactId.set(result.positioningArtifactId);
        this.generating.set(false);
        if (!isAuto) {
          this.messages.add({ severity: 'success', summary: this.t('growth.journey.positioning.generated') });
        }
        this.discoveryChanged.emit();
      },
      error: err => {
        this.generating.set(false);
        this.actionError.set(this.resolveActionError(err));
      }
    });
  }

  private resolveActionError(error: unknown): string {
    const code = this.extractErrorCode(error);
    if (code === 'growth_positioning_swot_not_approved' || code === 'growth_swot_not_approved') {
      return this.t('growth.journey.positioning.errors.swotNotApproved');
    }

    if (code === 'growth_discovery_not_found') {
      return this.t('common.notFound');
    }

    return code ?? this.t('common.error');
  }

  private extractErrorCode(error: unknown): string | null {
    if (!(error instanceof HttpErrorResponse)) {
      return null;
    }

    const body = error.error as Record<string, unknown> | null;
    if (!body) {
      return null;
    }

    if (typeof body['error'] === 'string') {
      return body['error'];
    }

    if (typeof body['Error'] === 'string') {
      return body['Error'];
    }

    return null;
  }
}
